import { useTheme } from '@emotion/react'
import { TheWestSection } from '@news-mono/common'
import {
    BreachEvent,
    click_origin_breach_screen,
    DataLayerEventName,
    useToggleState,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import React, { useEffect, useState } from 'react'
import {
    DEFAULT_CART_QUANTITY,
    toItemListId,
    useSection,
} from '../../../../../..'
import { getAttribution } from '../../../../../../errors/ErrorPage'
import { useFeatureForSection } from '../../../../../../feature-toggling'
import { LinkContext } from '../../../../../../navigation'
import { useAuthActions } from '../../../../../authentication/AuthenticationProvider'
import { SubscriptionPackages } from '../../../../common'
import { createLinkClickEvent } from '../../../../createLinkClickHandler'
import SubscriptionPackage, {
    checkIsRegional,
} from '../../../SubscriptionPackage/SubscriptionPackage'
import { WestRewardsMessage } from '../../shared-components/WestRewardsMessage/WestRewardsMessage'
import { WestRewards } from '../../WestRewards/WestRewards'
import {
    StyledBreachWallContent,
    StyledBreachWallContent2022,
    StyledButton,
    StyledHelpMessage,
    StyledPackage,
    StyledPackage2022,
    StyledRegistrationInfo,
    StyledTerms2022,
    StyledTextLink,
} from './BreachWallContent.styled'

export interface BreachWallContentProps {
    title: () => JSX.Element | null
    terms: () => JSX.Element | null
    mediaBanner: () => JSX.Element | null
    subscriptionPackages?: SubscriptionPackages
    breachCreativeName: string
    breachType: string
    breachDeal: string
    onEvent: (event: BreachEvent) => void
}

const backgroundImagePrefix = './thewest-digital-hero-variation-'
export const BreachWallContent: React.FC<BreachWallContentProps> = ({
    title,
    subscriptionPackages,
    breachCreativeName,
    breachType,
    breachDeal,
    onEvent,
    mediaBanner,
    terms,
}) => {
    const renderMediaBanner = mediaBanner
    const renderTitleSection = title
    const renderTerms = terms
    const { onSubscribeClick } = useAuthActions()
    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'
    const section = useSection<TheWestSection>()

    const isNewDesign2022 = useFeatureForSection({
        feature: 'tp-subscription-new-breach-package-background',
        sections: TheWestSections.default,
    })

    const backgroundVariation =
        useToggleState()?.['tp-subscription-hero-variation'] || 'a'

    const breach190Year = breachDeal === '190BD23'

    const abTestVariation =
        breachType === 'abtest-b-1x4'
            ? 'b'
            : breachType === 'abtest-c-50cx8'
            ? 'c'
            : breachType === 'abtest-d-50px8'
            ? 'd'
            : 'a'

    const isRegional = checkIsRegional(section)

    const [isLoading, setIsLoading] = useState(true)

    const [breachStyle, setBreachStyle] = useState(
        abTestVariation && abTestVariation !== 'a' && !isRegional
            ? 'abtest-container'
            : 'default-breachwall',
    )

    const onAllPackagesClicked = React.useCallback(() => {
        createLinkClickEvent(breachCreativeName, breachDeal, onEvent)()
        onSubscribeClick({
            callToAction: click_origin_breach_screen,
        })
    }, [breachCreativeName, breachDeal, onEvent, onSubscribeClick])

    const ContentContainer = isNewDesign2022
        ? StyledBreachWallContent2022
        : StyledBreachWallContent
    const PackageContainer = isNewDesign2022 ? StyledPackage2022 : StyledPackage
    let largeImage = ''
    let smallImage = ''
    if (isNewDesign2022 || abTestVariation === 'a') {
        try {
            largeImage = require(`${backgroundImagePrefix}${backgroundVariation}.jpg`)
        } catch {
            largeImage = require(`${backgroundImagePrefix}a.jpg`)
        }
        try {
            smallImage = require(`${backgroundImagePrefix}${backgroundVariation}-cropped.jpg`)
        } catch {
            smallImage = require(`${backgroundImagePrefix}a-cropped.jpg`)
        }
    }

    useEffect(() => {
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            subscriptionPackages?.map((pkg, index) => ({
                index,
                deal_name: breachType,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                item_list_id: toItemListId(breachCreativeName),
                price: pkg.weeklyPrice,
                promo_price: pkg.promoPrice || 0,
                discount: pkg.discount || 0,
                coupon: pkg.offerCode || '',
                quantity: DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            })) || []

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps

        // ABTEST container style override
        abTestVariation && abTestVariation !== 'a' && !isRegional
            ? setBreachStyle('abtest-container')
            : breach190Year && !abTestVariation
            ? setBreachStyle('breach-190-year')
            : setBreachStyle('default-breachwall')

        abTestVariation &&
            abTestVariation === 'a' &&
            setBreachStyle('default-breachwall')

        setIsLoading(false)
    }, [
        isLoading,
        isRegional,
        abTestVariation,
        breach190Year,
        breachCreativeName,
        masthead,
        onEvent,
        breachType,
        subscriptionPackages,
    ])

    if (!isLoading) {
        return (
            <>
                <ContentContainer
                    theme={theme}
                    backgroundColor={isNewDesign2022 ? '#2B2D2E' : undefined}
                    largeImage={largeImage}
                    smallImage={smallImage}
                    className={breachStyle}
                >
                    {renderMediaBanner()}
                    <StyledRegistrationInfo>
                        {!isNewDesign2022 &&
                            !breachStyle &&
                            renderTitleSection()}
                        {abTestVariation !== 'a' &&
                            breachStyle !== 'default-breachwall' &&
                            renderTitleSection()}
                    </StyledRegistrationInfo>
                    <PackageContainer
                        breachStyle={breachStyle}
                        abTestStyle={
                            breachStyle === 'abtest-container' ? true : false
                        }
                    >
                        {subscriptionPackages &&
                            subscriptionPackages.map(
                                (subscriptionPackageItem, index) => (
                                    <SubscriptionPackage
                                        key={index}
                                        breachCreativeName={breachCreativeName}
                                        breachDeal={breachDeal}
                                        onEvent={onEvent}
                                        packageInfo={subscriptionPackageItem}
                                        masthead={masthead}
                                        abTestingVar={
                                            abTestVariation &&
                                            !isRegional &&
                                            abTestVariation
                                        }
                                    />
                                ),
                            )}
                        {(isNewDesign2022 || breachStyle) && (
                            <LinkContext.Consumer>
                                {(value) => {
                                    const footerLinks = Array.isArray(
                                        value?.links?.footerTermsLinks,
                                    )
                                        ? value.links.footerTermsLinks
                                        : value?.links?.footerTermsLinks
                                              ?.default
                                    return (
                                        <StyledTerms2022
                                            breachStyle={breachStyle}
                                        >
                                            See other{' '}
                                            <a
                                                href="#"
                                                onClick={onAllPackagesClicked}
                                            >
                                                subscription options
                                            </a>{' '}
                                            and{' '}
                                            <a
                                                href={
                                                    abTestVariation === 'b'
                                                        ? 'https://subscriber.thewest.com.au/subscription-terms/first-month-offer'
                                                        : abTestVariation ===
                                                              'c' ||
                                                          abTestVariation ===
                                                              'd'
                                                        ? 'https://subscriber.thewest.com.au/subscription-terms/8weeks-50-off-offer'
                                                        : 'https://subscriber.thewest.com.au/subscription-terms'
                                                }
                                                target="_blank"
                                            >
                                                Terms &amp; Conditions
                                            </a>
                                        </StyledTerms2022>
                                    )
                                }}
                            </LinkContext.Consumer>
                        )}
                    </PackageContainer>
                    {!isNewDesign2022 && !breachStyle && (
                        <>
                            <StyledButton onClick={onAllPackagesClicked}>
                                View All Packages
                            </StyledButton>
                            <StyledHelpMessage>
                                Need Help? Call us at{' '}
                                <StyledTextLink
                                    reverseColor={true}
                                    href="tel:1800 811 855"
                                >
                                    1800 811 855
                                </StyledTextLink>
                            </StyledHelpMessage>
                            <WestRewardsMessage />
                            {renderTerms()}
                        </>
                    )}
                </ContentContainer>
                {(isNewDesign2022 || breachStyle) && <WestRewards />}
            </>
        )
    }

    return <></>
}

BreachWallContent.displayName = 'BreachWallContent'

export default BreachWallContent
